import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/Footer.css';

gsap.registerPlugin(ScrollTrigger);

const cols = [
  {
    heading: 'Product',
    links: [
      { label: 'CVE Lookup', href: '#cve-lookup' },
      { label: 'Our Process', href: '#process' },
      { label: 'Blogs', href: 'https://blogs.cvearity.com', external: true },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'Why CVEarity', href: '#why' },
      { label: 'Security News', href: '#news' },
      { label: 'FAQ', href: '#faq' },
    ],
  },
  {
    heading: 'Legal',
    links: [
      { label: 'Terms & Conditions', href: '/terms-and-conditions', external: false, routerLink: true },
      { label: 'Privacy Policy', href: '/privacy-policy', external: false, routerLink: true },
    ],
  },
];

const socials = [
  {
    label: 'X (Twitter)',
    href: 'https://x.com/cvearity',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.258 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://linkedin.com/company/cvearity',
    icon: (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
];

export default function Footer() {
  const footerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollTo = (href) => {
    if (!href.startsWith('#')) return;
    if (location.pathname !== '/') {
      navigate('/' + href);
    } else {
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.footer__brand', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.footer',
          start: 'top 85%',
        },
      });

      gsap.from('.footer__col', {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.footer__top',
          start: 'top 80%',
        },
      });

      gsap.from('.footer__bottom', {
        y: 20,
        opacity: 0,
        duration: 0.6,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.footer__bottom',
          start: 'top 95%',
        },
      });
    }, footerRef);

    return () => ctx.revert();
  }, []);

  const handleSocialHover = (e) => {
    gsap.to(e.currentTarget, {
      y: -4,
      scale: 1.15,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleSocialLeave = (e) => {
    gsap.to(e.currentTarget, {
      y: 0,
      scale: 1,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  return (
    <footer className="footer" ref={footerRef}>

      <div className="footer__top container">
        <div className="footer__brand">
          <div className="footer__logo">
            <img
              src={process.env.PUBLIC_URL + '/CVEarity White with Transparent BG.png'}
              alt="CVEarity"
              className="footer__logo-img"
            />
          </div>
          <p className="footer__tagline">
            Advanced vulnerability intelligence for modern security teams.
            Bringing clarity to cybersecurity chaos.
          </p>
          <div className="footer__status">
            <span className="footer__status-dot" />
            <span className="font-mono">All systems operational</span>
          </div>
          <div className="footer__socials">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="footer__social"
                aria-label={s.label}
                title={s.label}
                onMouseEnter={handleSocialHover}
                onMouseLeave={handleSocialLeave}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {cols.map((col) => (
          <div key={col.heading} className="footer__col">
            <h4 className="footer__col-head">{col.heading}</h4>
            <ul className="footer__col-links">
              {col.links.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a href={l.href} target="_blank" rel="noopener noreferrer" className="footer__link">
                      {l.label}
                    </a>
                  ) : l.routerLink ? (
                    <Link to={l.href} className="footer__link" onClick={() => window.scrollTo(0,0)}>
                      {l.label}
                    </Link>
                  ) : (
                    <button className="footer__link" onClick={() => scrollTo(l.href)}>
                      {l.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="footer__bottom container">
        <p className="footer__copy">
          © {new Date().getFullYear()} CVEarity, Uncovering Risk Securing Tomorrow - Product of{' '}
          <a href="https://www.cybertix.com" target="_blank" rel="noopener noreferrer" className="footer__cybertix">
            Cybertix
          </a>
        </p>
        <div className="footer__legal-links">
          <Link to="/terms-and-conditions" className="footer__legal-link" onClick={() => window.scrollTo(0,0)}>
            Terms & Conditions
          </Link>
          <span className="footer__legal-sep">·</span>
          <Link to="/privacy-policy" className="footer__legal-link" onClick={() => window.scrollTo(0,0)}>
            Privacy Policy
          </Link>
        </div>
      </div>
    </footer>
  );
}
